import React, { useRef, useEffect, useMemo } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-grids';
import { Button, Box, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';

import { useAppSelector } from '../../../../../app/store';
import { AddressType } from '../../../../../services/RegistrationService/registrationSlice/';
import { AddressDialog } from '../../dialogs/AddressDialog';
import { useAddressActions } from './hooks';
import { ActionsTemplate, PreferredTemplate } from './templates';

export interface AddressGridProps {
  testId?: string;
}

/**
 * AddressGrid - Displays and manages patient addresses
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for actions and dialog state
 * - Clean component structure
 */
export const AddressGrid: React.FC<AddressGridProps> = ({ testId }) => {
  const gridRef = useRef<GridComponent>(null);
  const addresses =
    useAppSelector((state) => state.registration.currentRegistration?.addresses) || [];

  // Use custom hook for address actions
  const {
    dialogOpen,
    editingAddress,
    handleAddAddress,
    handleEditAddress,
    handleDeleteAddress,
    handleSaveAddress,
    handleDialogClose,
  } = useAddressActions(addresses);

  // Cleanup grid on unmount
  useEffect(() => {
    return () => {
      if (gridRef.current) {
        try {
          gridRef.current.destroy();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  // Create templates
  const actionTemplate = useMemo(
    () => (props: AddressType) => (
      <ActionsTemplate
        address={props}
        onEdit={handleEditAddress}
        onDelete={handleDeleteAddress}
      />
    ),
    [handleEditAddress, handleDeleteAddress]
  );

  const preferredTemplate = useMemo(
    () => (props: AddressType) => <PreferredTemplate isPreferred={props.isPreferred} />,
    []
  );

  return (
    <Box>
      {/* Header with Add Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
          Addresses
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddAddress}
          size="small"
          sx={{
            backgroundColor: '#162346',
            '&:hover': {
              backgroundColor: '#1a2a52',
            },
          }}
        >
          Add Address
        </Button>
      </Box>

      {/* Syncfusion Grid */}
      <GridComponent
        ref={gridRef}
        dataSource={addresses}
        height={150}
        allowSorting={false}
        allowPaging={false}
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective field="addressType" headerText="Type" width="100" />
          <ColumnDirective field="street" headerText="Street" width="200" />
          <ColumnDirective field="street2" headerText="Street 2" width="150" />
          <ColumnDirective field="city" headerText="City" width="120" />
          <ColumnDirective field="state" headerText="State" width="80" />
          <ColumnDirective field="zip" headerText="ZIP" width="100" />
          <ColumnDirective
            field="isPreferred"
            headerText="Preferred"
            width="100"
            template={preferredTemplate}
          />
          <ColumnDirective
            headerText="Actions"
            width="120"
            template={actionTemplate}
            allowSorting={false}
          />
        </ColumnsDirective>
      </GridComponent>

      {/* Address Dialog */}
      <AddressDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSaveAddress}
        address={editingAddress}
        title={editingAddress ? 'Edit Address' : 'Add New Address'}
      />
    </Box>
  );
};

export default AddressGrid;
